// Angular
import { Injectable } from '@angular/core';

/**
 * Minimal shape of an event returned by GetAllEventsNameAndId
 */
export interface IFavoriteTrackEvent {
  EventId: string;
  EventName: string;
}

/**
 * Favorite/untracked split of the available events
 */
export interface IPartitionedEvents {
  favorite: IFavoriteTrackEvent[];
  untracked: IFavoriteTrackEvent[];
}

/**
 * Pure business logic for the Favorite Tracks component.
 */
@Injectable({ providedIn: 'root' })
export class FavoriteTracksService {
  /**
   * Split available events into favorite and untracked lists
   * @param {IFavoriteTrackEvent[]} allEvents All available events
   * @param {string[]} favorites Current ListOfFavorites (event ids)
   * @returns {IPartitionedEvents} Favorite and untracked events
   */
  partitionEvents(
    allEvents: IFavoriteTrackEvent[],
    favorites: string[]
  ): IPartitionedEvents {
    const events = allEvents ?? [];
    const ids = favorites ?? [];

    return {
      favorite: events.filter((e) => ids.includes(e.EventId)),
      untracked: events.filter((e) => !ids.includes(e.EventId))
    };
  }

  /**
   * Filter events whose name contains the search term (case-insensitive)
   * @param {IFavoriteTrackEvent[]} events Events to filter
   * @param {string} term Search term
   * @returns {IFavoriteTrackEvent[]} Matching events
   */
  filterByName(events: IFavoriteTrackEvent[], term: string): IFavoriteTrackEvent[] {
    if (!term) return events;
    const needle = term.toLowerCase();
    return (events ?? []).filter((e) => e.EventName.toLowerCase().includes(needle));
  }

  /**
   * Merge selected ids into the favorites list without duplicates
   * @param {string[]} favorites Current favorites
   * @param {string[]} selected Ids to add
   * @returns {string[]} Updated, de-duplicated favorites
   */
  addFavorites(favorites: string[], selected: string[]): string[] {
    return Array.from(new Set([...(favorites ?? []), ...(selected ?? [])]));
  }

  /**
   * Remove a single id from the favorites list
   * @param {string[]} favorites Current favorites
   * @param {string} eventId Id to remove
   * @returns {string[]} Updated favorites
   */
  removeFavorite(favorites: string[], eventId: string): string[] {
    return (favorites ?? []).filter((id) => id !== eventId);
  }
}
